import React, { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/SearchableSelect';
import { useCounterpartySearch } from '@/hooks/counterparty';
import { useFitTabs, visibleTabsOf } from './fit-tabs';

// Styles and separators mirror the Trades views tab bar (ViewTabBar / ViewTab), except a
// tighter top padding (pt-2, not pt-3): the strip sits flush under the Collateral Manager leg
// tabs. Folder/card tabs, NOT the top-level blue-underline style. Active = white card with
// top/side borders + rounded top whose white body covers the divider beneath it, so the
// active tab reads as connected to the content below.
const tabClasses = (active: boolean): string =>
  clsx(
    'inline-flex min-w-0 items-center px-4 py-2.5 text-sm font-medium motion-safe:transition-colors whitespace-nowrap cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
    active
      ? 'bg-white text-slate-900 font-semibold border-t border-x border-slate-300 rounded-t-lg relative z-10'
      : 'text-slate-500 bg-transparent rounded-md hover:bg-slate-200 hover:text-slate-800',
  );

const MENU_BUTTON_CLASSES =
  'inline-flex items-center gap-1 px-4 py-2.5 text-sm font-medium text-slate-500 bg-transparent rounded-md hover:bg-slate-200 hover:text-slate-800 motion-safe:transition-colors whitespace-nowrap cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2';

const SEPARATOR_CLASSES = 'h-4 w-px flex-shrink-0 motion-safe:transition-opacity duration-150';

export interface MtmCounterpartyTabsProps {
  counterparties: { id: number; name: string }[];
  activeId: number | undefined;
  onTabChange: (id: number) => void;
  isLoading: boolean;
  /** The rest of the page's tab list beyond `counterparties`, listed under "+N more" while
   *  the search box is empty and paged in from the server. */
  rest?: {
    options: SearchableSelectOption[];
    /** Rows of the list not loaded yet. */
    unloadedCount: number;
    hasMore: boolean;
    onLoadMore: () => void;
    isLoadingMore: boolean;
  };
}

/**
 * The tabs that fit the row, then a menu at the end of it: with an empty search it lists the
 * tabs that did not fit and the `rest` of the page's tab list; a typed query searches the
 * WHOLE registry server-side, deactivated counterparties included. The registry itself is
 * never loaded here, only searched a page at a time.
 */
export const MtmCounterpartyTabs: React.FC<MtmCounterpartyTabsProps> = ({
  counterparties,
  activeId,
  onTabChange,
  isLoading,
  rest,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  // An empty search box lists the tabs that did not fit, which is what "+N more" promises;
  // a typed query searches the whole registry, and only then is the registry asked.
  const searching = query !== '';
  const search = useCounterpartySearch(query, false, { enabled: searching });

  const activeIndex = counterparties.findIndex((cp) => cp.id === activeId);
  const { rowRef, measureRef, menuMeasureRef, visibleCount, menuOpensLeft } = useFitTabs(
    activeIndex,
    counterparties.length,
    counterparties,
    isLoading,
  );

  const visibleTabs = useMemo(
    () => visibleTabsOf(counterparties, visibleCount, activeIndex),
    [counterparties, visibleCount, activeIndex],
  );
  const restCount = rest ? rest.options.length + rest.unloadedCount : 0;
  const hiddenCount = counterparties.length - visibleTabs.length + restCount;
  const hiddenOptions = useMemo(() => {
    const visibleIds = new Set(visibleTabs.map((cp) => cp.id));
    return [
      ...counterparties
        .filter((cp) => !visibleIds.has(cp.id))
        .map((cp) => ({ value: String(cp.id), label: cp.name })),
      ...(rest?.options ?? []),
    ];
  }, [counterparties, visibleTabs, rest?.options]);

  if (isLoading) {
    return (
      <div className="flex items-end bg-slate-50 px-4 pt-2 shadow-[inset_0_-1px_0_0_#cbd5e1]">
        <span className="px-4 py-2.5 text-sm font-medium text-slate-300">Loading...</span>
      </div>
    );
  }

  return (
    // The bottom divider is an INSET box-shadow (not border-b): being painted inside the box,
    // the active tab's white background paints over it, so the active tab has no line under it.
    <div
      data-tour="mtm-counterparty-tabs"
      className="flex items-end bg-slate-50 px-4 pt-2 shadow-[inset_0_-1px_0_0_#cbd5e1]"
    >
      <div ref={rowRef} className="relative flex min-w-0 flex-1 items-center gap-1">
        <div
          ref={measureRef}
          className="pointer-events-none invisible absolute flex items-center"
          style={{ height: 0, overflow: 'hidden' }}
          aria-hidden="true"
        >
          {counterparties.map((cp) => (
            <span key={cp.id} className={tabClasses(cp.id === activeId)}>
              <span className="truncate max-w-[160px]">{cp.name}</span>
            </span>
          ))}
        </div>
        {/* The menu button bare, and with the widest "+N more" it can show.
            NOTE: reserved for the digits of the whole count, so with fewer hidden (12 tabs,
            9 hidden) one digit's width (~8 px) stays unused; the price of a fit that does
            not depend on its own result. */}
        <div
          ref={menuMeasureRef}
          className="pointer-events-none invisible absolute flex items-center"
          style={{ height: 0, overflow: 'hidden' }}
          aria-hidden="true"
        >
          <span className={MENU_BUTTON_CLASSES}>
            <ChevronDownIcon className="h-4 w-4" />
          </span>
          <span className={MENU_BUTTON_CLASSES}>
            <span className="tabular-nums">+{counterparties.length + restCount} more</span>
            <ChevronDownIcon className="h-4 w-4" />
          </span>
        </div>
        <div className="flex min-w-0 items-center gap-1" role="tablist" aria-label="Counterparty">
          {visibleTabs.map((cp, index) => {
            const active = cp.id === activeId;
            const separatorHidden =
              active ||
              visibleTabs[index - 1]?.id === activeId ||
              hoveredIndex === index - 1 ||
              hoveredIndex === index;
            return (
              <React.Fragment key={cp.id}>
                {index > 0 && (
                  <div
                    aria-hidden="true"
                    className={clsx(SEPARATOR_CLASSES, separatorHidden ? 'opacity-0' : 'bg-slate-300/60')}
                  />
                )}
                <button
                  type="button"
                  role="tab"
                  onClick={() => onTabChange(cp.id)}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  aria-current={active ? 'page' : undefined}
                  aria-selected={active}
                  title={cp.name}
                  className={tabClasses(active)}
                >
                  <span className="truncate max-w-[160px]">{cp.name}</span>
                </button>
              </React.Fragment>
            );
          })}
        </div>
        <div className="flex flex-shrink-0 items-center gap-1">
          {/* Always rendered, only hidden without tabs: the room reserved for the menu counts it. */}
          <div
            aria-hidden="true"
            className={clsx(
              'h-4 w-px flex-shrink-0',
              visibleTabs.length > 0 ? 'bg-slate-300/60' : 'opacity-0',
            )}
          />
          <SearchableSelect
            value={activeId !== undefined ? String(activeId) : ''}
            onChange={(value) => onTabChange(Number(value))}
            options={searching ? search.options : hiddenOptions}
            onSearchChange={setQuery}
            isLoading={searching && search.isLoading}
            isError={searching && search.isError}
            hasMore={searching ? search.hasNextPage : (rest?.hasMore ?? false)}
            onLoadMore={() => (searching ? void search.fetchNextPage() : rest?.onLoadMore())}
            isLoadingMore={searching ? search.isFetchingNextPage : (rest?.isLoadingMore ?? false)}
            searchPlaceholder="Search counterparties…"
            emptyMessage={searching ? undefined : 'Type to search all counterparties'}
            menuClassName={clsx(
              'top-full mt-1 w-72 border-slate-200',
              menuOpensLeft ? 'right-0' : 'left-0',
            )}
            triggerClassName={MENU_BUTTON_CLASSES}
            triggerContent={
              <>
                {hiddenCount > 0 && <span className="tabular-nums">+{hiddenCount} more</span>}
                <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
              </>
            }
            buttonProps={{
              'aria-label':
                hiddenCount > 0
                  ? `+${hiddenCount} more, search all counterparties`
                  : 'Search all counterparties',
              'data-testid': 'mtm-counterparty-menu',
            }}
          />
        </div>
      </div>
    </div>
  );
};
